import { useEffect, useRef } from "react";
import SEO from "@/components/SEO";
import { motion, useInView } from "framer-motion";
import {
  BarChart3, CreditCard, Banknote, LinkIcon, ShoppingCart, FileText,
  Package, TrendingUp, Coins, AlertTriangle, Building2, ScrollText,
  UserCheck, MapPin, MessageCircle, BookOpen, Shield, Smartphone,
  Lock, Bell, Globe, FileSpreadsheet, Handshake, ArrowRight,
  UtensilsCrossed, Store, Monitor, User
} from "lucide-react";
import { useTranslation } from "@/i18n/translations";

const DASHBOARD_IMG = "https://customer-assets.emergentagent.com/job_payment-links-hub-1/artifacts/32gh38ba_Capture%20d%E2%80%99e%CC%81cran%202026-05-08%20a%CC%80%2018.09.27.png";

function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }} transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>{children}</motion.div>
  );
}

// Feature titles use English-fallback keys via translations dict for app features
// We keep the icons here but translate the labels via the existing 'features' namespace where possible
const FEATURE_ICONS = [
  BarChart3, CreditCard, Banknote, LinkIcon, ShoppingCart, FileText,
  Package, TrendingUp, Coins, AlertTriangle, Building2, ScrollText,
  UserCheck, MapPin, MessageCircle, BookOpen,
];

// Static FR descriptions kept as fallback text — the 16 feature cards translations
// would require very large dictionaries; we expose them under appFeat.featDesc for now
// Each feature card title/desc is rendered from a per-language list below
const getFeatures = (tr) => {
  // The detailed 16-feature list — we provide a single i18n hint via featDesc
  // and keep functional French copy as fallback (most-used language)
  const list = [
    { title: "Tableau de bord en temps reel", desc: "CA brut & net, transactions par jour/semaine/mois, pourboires." },
    { title: "Transactions", desc: "Historique complet. Filtres par date, montant, methode. Export PDF." },
    { title: "Versements (Payouts)", desc: "Suivi des virements bancaires, calendrier, historique." },
    { title: "Liens de paiement", desc: "Generation en un clic, envoi par SMS, email ou WhatsApp." },
    { title: "Integration E-Commerce", desc: "WooCommerce, Shopify, PrestaShop, Magento. Cles API." },
    { title: "Factures suisses (Swiss QR-bill)", desc: "Standard SCOR, rapprochement automatique CAMT.054." },
    { title: "Bons de livraison", desc: "Bons lies a une facture, signature ecran, PDF auto." },
    { title: "Rapports financiers", desc: "Exports comptables (CA brut, net, frais, commissions)." },
    { title: "Pourboires (Tips)", desc: "Suivi automatique des pourboires terminaux POS." },
    { title: "Litiges (Disputes)", desc: "Alertes temps reel, soumission de preuves, suivi." },
    { title: "Financement (Capital)", desc: "Pre-financement et leasing depuis l'app." },
    { title: "Contrats", desc: "Visualisation, signature electronique des avenants." },
    { title: "Verification KYC digitale", desc: "Onboarding 100% digital depuis votre mobile." },
    { title: "Adresses & Stores", desc: "Gestion de vos points de vente et boutiques." },
    { title: "Support Chat integre", desc: "Conversation directe avec l'equipe AyaPay 7j/7." },
    { title: "Guide d'utilisation", desc: "Documentation interactive, FAQ, prise en main rapide." },
  ];
  return list.map((f, i) => ({ ...f, icon: FEATURE_ICONS[i] }));
};

export default function FeaturesPage() {
  const { t: tr } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const FEATURES = getFeatures(tr);

  const TRANSVERSAL = [
    { icon: Shield, label: "Conforme Suisse", desc: "Swiss QR-bill, CAMT.054, multi-devises CHF/EUR" },
    { icon: Smartphone, label: "100% responsive", desc: "Desktop, tablette, mobile" },
    { icon: Lock, label: "PCI-DSS", desc: "Niveau 1" },
    { icon: Bell, label: "Notifications", desc: "Push & email" },
    { icon: Globe, label: "Multi-langues", desc: "FR, DE, IT, EN, PT, TR" },
    { icon: FileSpreadsheet, label: "Exports comptables", desc: "Fiduciaires suisses" },
    { icon: Handshake, label: "Partenaires", desc: "Commissions auto" },
  ];

  const PERSONAS = [
    { icon: UtensilsCrossed, title: "Restaurants & Cafes", desc: "Pourboires, Pay@Table, multi-terminaux." },
    { icon: Store, title: "Commerces de detail", desc: "Stock, fidelite, exports, Swiss QR-bill." },
    { icon: Monitor, title: "E-commercants", desc: "WooCommerce/Shopify, liens, rapports." },
    { icon: User, title: "Independants", desc: "Facturation suisse, KYC digital, support 7j/7." },
  ];

  const PWA_DEVICES = [
    { icon: Monitor, title: tr("appFeat.desktop"), desc: tr("appFeat.desktopD") },
    { icon: Smartphone, title: tr("appFeat.mobilePwa"), desc: tr("appFeat.mobilePwaD") },
    { icon: BarChart3, title: tr("appFeat.tablet"), desc: tr("appFeat.tabletD") },
  ];

  const DASHBOARD_BENEFITS = [
    tr("appFeat.dbF1"), tr("appFeat.dbF2"), tr("appFeat.dbF3"),
    tr("appFeat.dbF4"), tr("appFeat.dbF5"),
  ];

  return (
    <div data-testid="features-page">
      <SEO page="features" />
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 bg-[#0A1128]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("appFeat.tag")}</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mt-4 leading-[1.1]">
                {tr("appFeat.title")}
              </h1>
              <p className="text-base md:text-lg text-slate-300 mt-6 leading-relaxed">
                {tr("appFeat.desc")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <a href="https://pay.ayapay.ch/register" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 font-semibold bg-[#00C853] text-white rounded-full px-7 py-3 text-sm hover:bg-[#00A844] transition-all">
                  {tr("appFeat.create")} <ArrowRight size={16} />
                </a>
                <a href="https://pay.ayapay.ch/login" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center font-semibold border-2 border-white/20 text-white rounded-full px-7 py-3 text-sm hover:bg-white/10 transition-all">
                  {tr("appFeat.login")}
                </a>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/40">
                <img src={DASHBOARD_IMG} alt="AyaPay Dashboard" loading="lazy" className="w-full h-auto" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-10 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: "16+", label: tr("appFeat.kn1") },
              { value: "100%", label: tr("appFeat.kn2") },
              { value: "PCI-DSS", label: tr("appFeat.kn3") },
              { value: "PWA", label: tr("appFeat.kn4") },
            ].map((s, i) => (
              <AnimatedSection key={i} delay={i * 0.08} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-[#1B2B5B]">{s.value}</div>
                <div className="text-xs md:text-sm text-[#475569] mt-1">{s.label}</div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("appFeat.featTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">
              {tr("appFeat.featTitle")}
            </h2>
            <p className="text-base text-[#475569] mt-4 max-w-xl mx-auto">
              {tr("appFeat.featDesc")}
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map((feat, i) => (
              <AnimatedSection key={i} delay={i * 0.04}>
                <div className="feature-card bg-white rounded-xl border border-slate-200 p-6 h-full">
                  <div className="w-10 h-10 rounded-lg bg-[#00C853]/10 flex items-center justify-center mb-4">
                    <feat.icon size={20} className="text-[#00C853]" />
                  </div>
                  <h3 className="text-sm font-semibold text-[#0A1128] mb-1.5">{feat.title}</h3>
                  <p className="text-xs text-[#475569] leading-relaxed">{feat.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <AnimatedSection>
              <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
                <img src={DASHBOARD_IMG} alt="AyaPay Dashboard" loading="lazy" className="w-full h-auto" />
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("appFeat.dbTag")}</span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">
                {tr("appFeat.dbTitle")}
              </h2>
              <p className="text-base text-[#475569] mt-4 leading-relaxed">
                {tr("appFeat.dbDesc")}
              </p>
              <ul className="mt-6 space-y-3">
                {DASHBOARD_BENEFITS.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#00C853]/10 flex items-center justify-center shrink-0 mt-0.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#00C853]" />
                    </div>
                    <span className="text-sm text-[#475569]">{item}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("appFeat.whoTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">
              {tr("appFeat.whoTitle")}
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PERSONAS.map((p, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="feature-card bg-white rounded-xl border border-slate-200 p-7 h-full text-center">
                  <div className="w-12 h-12 rounded-xl bg-[#1B2B5B]/5 flex items-center justify-center mb-5 mx-auto">
                    <p.icon size={24} className="text-[#1B2B5B]" />
                  </div>
                  <h3 className="text-base font-semibold text-[#0A1128] mb-2">{p.title}</h3>
                  <p className="text-xs text-[#475569] leading-relaxed">{p.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#1B2B5B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4 md:gap-6">
            {TRANSVERSAL.map((tx, i) => (
              <AnimatedSection key={i} delay={i * 0.05} className="text-center">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center mx-auto mb-3">
                  <tx.icon size={18} className="text-[#00C853]" />
                </div>
                <div className="text-xs font-semibold text-white">{tx.label}</div>
                <div className="text-[10px] text-slate-400 mt-0.5 leading-tight">{tx.desc}</div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection>
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("appFeat.pwaTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">
              {tr("appFeat.pwaTitle")}
            </h2>
            <p className="text-base text-[#475569] mt-4 max-w-xl mx-auto leading-relaxed">
              {tr("appFeat.pwaDesc")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
              {PWA_DEVICES.map((d, i) => (
                <AnimatedSection key={i} delay={i * 0.1}>
                  <div className="bg-[#F8F9FA] rounded-xl border border-slate-100 p-6">
                    <div className="w-10 h-10 rounded-lg bg-[#00C853]/10 flex items-center justify-center mx-auto mb-4">
                      <d.icon size={20} className="text-[#00C853]" />
                    </div>
                    <h3 className="text-sm font-semibold text-[#0A1128] mb-1">{d.title}</h3>
                    <p className="text-xs text-[#475569] leading-relaxed">{d.desc}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#0A1128]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              {tr("appFeat.ctaTitle")}
            </h2>
            <p className="text-base text-slate-300 mt-4 max-w-md mx-auto">
              {tr("appFeat.ctaDesc")}
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 mt-8">
              <a href="https://pay.ayapay.ch/register" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 font-semibold bg-[#00C853] text-white rounded-full px-8 py-3.5 text-sm hover:bg-[#00A844] transition-all">
                {tr("appFeat.create")} <ArrowRight size={16} />
              </a>
              <a href="https://pay.ayapay.ch/login" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center font-semibold border-2 border-white/20 text-white rounded-full px-8 py-3.5 text-sm hover:bg-white/10 transition-all">
                {tr("appFeat.login")}
              </a>
              <a href="mailto:service@ayapay.ch" className="inline-flex items-center justify-center font-semibold border-2 border-white/20 text-white rounded-full px-8 py-3.5 text-sm hover:bg-white/10 transition-all">
                service@ayapay.ch
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
